import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { catchError, concatMap, map, toArray } from 'rxjs/operators';
import { LigneGroupee } from './saisie-groupee.component';

/**
 * Suppression de plusieurs éléments cochés : confirmation par saisie du nombre, suppressions l'une
 * après l'autre, résultat par ligne, nouvel essai limité aux lignes en erreur.
 */
@Component({
  selector: 'bay-suppression-groupee',
  template: `
    <div data-etat="confirmation">
      <p class="mb-2" style="font-size:14px;color:#991b1b;">{{ avertissement }}</p>
      <label class="bay-label" for="sgr-confirmer">Tapez {{ lignes.length }} pour confirmer</label>
      <input id="sgr-confirmer" class="bay-input mb-3" inputmode="numeric" [(ngModel)]="confirmation" [disabled]="envoi">
    </div>
    <ul class="list-unstyled mb-3" style="max-height:40vh;overflow:auto;">
      <li *ngFor="let l of lignes" class="d-flex justify-content-between py-1 border-bottom" [attr.data-ligne]="l.id" style="font-size:14px;">
        <span>{{ l.nom }} <span class="text-muted">{{ l.info }}</span></span>
        <span *ngIf="etat[l.id]?.ok === true" class="text-success">✓</span>
        <span *ngIf="etat[l.id]?.ok === false" class="text-danger" style="font-size:12px;">{{ etat[l.id]?.message }}</span>
      </li>
    </ul>
    <div *ngIf="erreur" class="text-danger mb-2" style="font-size:13px;" data-etat="erreur">{{ erreur }}</div>
    <div *ngIf="bilan" class="mb-2" style="font-size:13px;" data-etat="bilan">{{ bilan }}</div>
    <div class="d-flex gap-2">
      <button type="button" class="bay-btn bay-btn-danger bay-btn-sm" [disabled]="envoi || restantes.length === 0" (click)="supprimerTout()">
        {{ aDesErreurs ? 'Réessayer les lignes en erreur' : 'Supprimer ' + lignes.length + ' ' + libelle }}</button>
      <button type="button" class="bay-btn bay-btn-ghost bay-btn-sm" [disabled]="envoi" (click)="fermer.emit(nbOk > 0)">
        {{ nbOk ? 'Terminer' : 'Annuler' }}</button>
    </div>
  `
})
export class SuppressionGroupeeComponent implements OnChanges {
  @Input() lignes: LigneGroupee[] = [];
  @Input() supprimer!: (id: number) => Observable<unknown>;
  /** « lot(s) », « bassin(s) »… */
  @Input() libelle = 'élément(s)';
  @Input() avertissement = 'Ces éléments seront supprimés définitivement.';
  /** true : au moins une suppression a réussi */
  @Output() fermer = new EventEmitter<boolean>();

  confirmation = '';
  etat: Record<number, { ok: boolean; message?: string }> = {};
  envoi = false;
  erreur = '';
  bilan = '';

  ngOnChanges(): void {
    this.etat = {};
    this.confirmation = '';
    this.erreur = '';
    this.bilan = '';
  }

  get restantes(): LigneGroupee[] { return this.lignes.filter(l => this.etat[l.id]?.ok !== true); }
  get aDesErreurs(): boolean { return this.lignes.some(l => this.etat[l.id]?.ok === false); }
  get nbOk(): number { return this.lignes.filter(l => this.etat[l.id]?.ok === true).length; }

  supprimerTout(): void {
    this.erreur = '';
    if (this.confirmation.trim() !== String(this.lignes.length)) {
      this.erreur = `Tapez ${this.lignes.length} pour confirmer la suppression.`;
      return;
    }
    this.envoi = true;
    from(this.restantes).pipe(
      concatMap(l => this.supprimer(l.id).pipe(
        map(() => ({ id: l.id, ok: true, message: '' })),
        catchError(err => of({ id: l.id, ok: false,
          message: err?.error?.message || (err?.status === 0 ? 'Pas de connexion' : err?.status === 403 ? 'Non autorisé' : 'Échec') })))),
      toArray()
    ).subscribe(res => {
      res.forEach(r => this.etat[r.id] = r.ok ? { ok: true } : { ok: false, message: r.message });
      const ko = this.lignes.length - this.nbOk;
      this.bilan = `${this.nbOk} ${this.libelle} supprimé(s)` + (ko ? `, ${ko} en erreur` : '');
      this.envoi = false;
    });
  }
}
